import type { ReactNode } from 'react';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * STATUSMARKE (Auftrag 17.09.2026, Abschnitt 18; Abschnitt 10; Abschnitt 28)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   „Status nicht ausschließlich über Farbe vermitteln: ✓ Konform,
 *    ! Prüfen, × Nicht konform."
 *
 * Deshalb hat diese Marke KEINE Betriebsart ohne Text. `text` ist ein
 * Pflichtfeld, das Zeichen kommt automatisch dazu. Wer nur eine Farbe
 * setzen will, findet hier nichts - und das ist Absicht: Ein Farbpunkt
 * allein ist für einen Rot-Grün-Schwachen keine Information (rund acht
 * Prozent der männlichen Nutzer) und für eine Vorlesehilfe gar keine.
 *
 * Die fünf Zustände sind die des Auftrags (Success/Warning/Error/Info/
 * Neutral). Sie heißen hier `gut`, `warnung`, `fehler`, `info`, `neutral`
 * und liegen damit auf den Merkmalsnamen `--zustand-*` in
 * `stil/merkmale.css`; ein sechster wird nicht erfunden.
 *
 * Für die Koordination gilt Abschnitt 18 gesondert: eine offene
 * Koordinationsaufgabe ist `warnung`, nicht `fehler` - rot bleibt dem
 * tatsächlichen Problem vorbehalten.
 */

export type Zustand = 'gut' | 'warnung' | 'fehler' | 'info' | 'neutral';

/**
 * Das Zeichen je Zustand. Ausgeschrieben statt als Icon, weil es in
 * Tabellenzellen, in Marken und in kopiertem Text gleich funktioniert.
 */
export const ZUSTAND_ZEICHEN: Record<Zustand, string> = {
  gut: '✓',
  warnung: '!',
  fehler: '×',
  info: 'i',
  neutral: '–',
};

/** Vorlesetext des Zeichens - für Menschen, die das Glyph nicht sehen. */
export const ZUSTAND_WORT: Record<Zustand, string> = {
  gut: 'in Ordnung',
  warnung: 'zu prüfen',
  fehler: 'nicht in Ordnung',
  info: 'Hinweis',
  neutral: 'ohne Bewertung',
};

export interface StatusBadgeProps {
  zustand: Zustand;
  /** Pflicht. Ohne Text gäbe es nur Farbe - siehe Kopfkommentar. */
  text: ReactNode;
  /** Überschreibt das Standardzeichen, z. B. eine Zahl bei einer Zählmarke. */
  zeichen?: string;
  /** Erläuterung beim Überfahren; landet zusätzlich in `aria-label`. */
  titel?: string;
  /** Kleinere Ausführung für dichte Tabellen. */
  klein?: boolean;
  className?: string;
}

export function StatusBadge({
  zustand, text, zeichen, titel, klein, className,
}: StatusBadgeProps) {
  const glyph = zeichen ?? ZUSTAND_ZEICHEN[zustand];
  const klassen = ['m-marke', `m-marke-${zustand}`];
  if (klein) klassen.push('m-marke-klein');
  if (className) klassen.push(className);

  return (
    <span
      className={klassen.join(' ')}
      title={titel}
      // Das Glyph ist Dekoration, der Zustand steht im Vorlesetext; sonst
      // liest die Hilfe „mal Nicht konform" statt „Nicht konform".
      aria-label={titel ? `${ZUSTAND_WORT[zustand]}: ${titel}` : undefined}
    >
      <span className="m-marke-zeichen" aria-hidden="true">{glyph}</span>
      <span className="m-marke-text">{text}</span>
    </span>
  );
}

/**
 * Nur der Punkt - ausschließlich dort erlaubt, wo der Text unmittelbar
 * daneben steht (z. B. in einer Legende oder vor einem Zeilennamen). Er
 * trägt deshalb selbst keine Bedeutung und ist für Vorlesehilfen versteckt.
 */
export interface ZustandspunktProps {
  zustand: Zustand;
  titel?: string;
}

export function Zustandspunkt({ zustand, titel }: ZustandspunktProps) {
  return (
    <span
      className={`m-punkt m-punkt-${zustand}`}
      title={titel}
      aria-hidden="true"
    />
  );
}
